import React from "react";
import { Grid } from "@material-ui/core";
import { useSelector } from "react-redux";

import {
  ControlledLifeCycleInput,
  DollarDisplayCentsInputProps,
  PercentageInputProps,
  withNamespace,
} from "components/Form/FormikControls";

import Big from "big.js";
import { getDefaultBillingSettings } from "selectors/billingSettings";
import { getCurrentSale } from "selectors/currentSale";
import { getActiveLivestockAgentDeployment } from "selectors/deploymentActiveAgent";
import { CommonPaymentFields } from "./CommonPaymentMethodFields";

function calculateAmountIncludingFeeCents(
  paymentAmountCents: Big,
  cardFeePercentage: Big,
): Big {
  return paymentAmountCents.plus(paymentAmountCents.times(cardFeePercentage));
}

const TransactionFeeInputProps = {
  ...PercentageInputProps,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Input props passed from parent component
  formatFieldValue: (_fieldValue: number, inputProps: any) => {
    const { cardFeePercentageBig: feePercentage, multiplier } = inputProps;
    return feePercentage.times(multiplier);
  },
  disabled: true,
};

const AmountIncludingFeeInputProps = {
  ...DollarDisplayCentsInputProps,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Input props passed from parent component
  formatFieldValue: (_fieldValue: number, inputProps: any) => {
    const {
      paymentAmountCentsBig: paymentCents,
      cardFeePercentageBig: feePercentage,
      multiplier,
    } = inputProps;
    const amountIncludingFee = calculateAmountIncludingFeeCents(
      paymentCents,
      feePercentage,
    );
    return amountIncludingFee.times(multiplier);
  },
  disabled: true,
};

function getCardFeePercentage(
  currentSale: Sale,
  activeDeployment: Deployment,
  billingSettings: BillingSettings,
): number {
  const deploymentSale = currentSale?.deployment_sales?.find(
    (ds: DeploymentSale) => ds.deployment_id === activeDeployment?.id,
  );
  const deploymentSaleFee = deploymentSale?.card_transaction_fee_percentage;

  if (deploymentSaleFee > 0) {
    return deploymentSaleFee;
  }
  if (billingSettings?.cardTransactionFeePercentage > 0) {
    return billingSettings.cardTransactionFeePercentage;
  }
  return 0;
}

interface CardPaymentMethodFormProps {
  namespace?: string;
  paymentAmountCents: number;
}

export function CardPaymentMethodForm(
  props: CardPaymentMethodFormProps,
): React.JSX.Element {
  const { namespace: ns = "", paymentAmountCents } = props;

  const billingSettings = useSelector(getDefaultBillingSettings);
  const currentSale = useSelector(getCurrentSale);
  const activeDeployment = useSelector(getActiveLivestockAgentDeployment);

  const cardFeePercentage = getCardFeePercentage(
    currentSale,
    activeDeployment,
    billingSettings,
  );

  const paymentAmountCentsBig = new Big(paymentAmountCents);
  const cardFeePercentageBig = new Big(cardFeePercentage);

  const feePercentageDisplay = cardFeePercentageBig.times(100).toFixed(2);

  return (
    <>
      <Grid item sm={6}>
        <ControlledLifeCycleInput
          {...TransactionFeeInputProps}
          name={withNamespace(ns, "totalAmountCents")}
          cardFeePercentageBig={cardFeePercentageBig}
          label="Transaction Fee (%)"
        />
      </Grid>
      <Grid item sm={6}>
        <ControlledLifeCycleInput
          {...AmountIncludingFeeInputProps}
          name={withNamespace(ns, "totalAmountCents")}
          paymentAmountCentsBig={paymentAmountCentsBig}
          cardFeePercentageBig={cardFeePercentageBig}
          label={`Amount Including ${feePercentageDisplay}% Fee`}
        />
      </Grid>
      <CommonPaymentFields namespace={ns} />
    </>
  );
}
